/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef } from "react";
import EditProject from "./EditProject";
import { useLanguage } from "LanguageContext";
import { useNavigate } from "react-router-dom";
import DataTable, { sortingColumnsProps } from "../DataTable/DataTable";
import { THeaderProps } from "../DataTable/THeader";
import { css, ClassNames, useTheme } from "@emotion/react";
import ProjectsSearch from "../Projects/ProjectsSearch";
import { ProjectsModel } from "../../../services/projectsService";

interface ProjectsTableProps {
  projectData: any;
  onDelete: (analysisId: string) => void;
  onEdit: (projectId: string, updatedProject: any) => void;
  isTableDataLoading: boolean;
  sortingColumns: sortingColumnsProps;
  setSortingColumns: (sortingColumns: any) => void;
  setPageNo: (page: number) => void;
  pageNo:number;
  setRowsPerPage: (rowsPerPage: number) => void;
  rowsPerPage: number;
}

export default function ProjectsTable(props: Readonly<ProjectsTableProps>) {
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  const { rowsPerPage, setRowsPerPage } = props;
  const [openDialogId, setOpenDialogId] = useState<string | null>(null); 
  const [project, setProject] = useState<ProjectsModel | null>(null);
  const [projectList, setProjectList] = useState<any[] | null>([]);
   const [searchProject, setSearchProject] = useState<string | null>(null); 

  const navigate = useNavigate();
  const onDialogOpen = (analyseId: string) => setOpenDialogId(analyseId);
  const onDialogClose = () => setOpenDialogId(null);
  const theme = useTheme();
  const paginatedProjectsRef = useRef<any[]>([]);
  const totalRowsRef = useRef<number>(0);
  const totalRows = props.projectData?.count ?? 5;

  const handlePageChange = (page: number) => {
    setCurrentPage(page); 
    props.setPageNo(page);
  };
   const handleRowClick = (rowData: any, event: React.MouseEvent) => {
    if (openDialogId !== null) {
      event.stopPropagation();
      return;
    }
      if(rowData?.name){
        navigate(`/analyses?project=${rowData.name}-${rowData?.advertiser}`);
      }
      
    };

    const loadPagination = ()=>{
      let filteredProjects = props.projectData?.results;
      if (project?.id) {
        filteredProjects = props.projectData?.results?.filter(
          (proj: any) => project === null || proj?.id === project.id
        );
      } 
      // manual search 
      if(searchProject){
         let projectSearch = '';
         let advertiserSearch = '';
          if (searchProject?.includes('-')) {
              projectSearch = searchProject?.split('-')[0];
              advertiserSearch = searchProject?.split('-')[1];
              filteredProjects = props.projectData?.results?.filter(
                (proj: any) =>
                  proj.name.toLowerCase().includes(projectSearch.toLowerCase()) &&
                  proj.advertiser.toLowerCase().includes(advertiserSearch.toLowerCase())
              );              
          } else {
             filteredProjects = props.projectData?.results?.filter(
                 (proj: any) => proj.advertiser.toLowerCase().includes(searchProject.toLowerCase()) ||  proj.name.toLowerCase().includes(searchProject.toLowerCase())
                );
          }
      }     

       totalRowsRef.current = filteredProjects.length || 0;
        paginatedProjectsRef.current = filteredProjects.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage
        );
        setProjectList(paginatedProjectsRef.current || [])       
    }
    
      useEffect(()=>{
        if(currentPage &&  rowsPerPage ){
          loadPagination();
        }
      },[currentPage, rowsPerPage])
    
      useEffect(()=>{
        loadPagination();      
      },[project, searchProject, props.projectData]) 
    
      useEffect(()=>{
        setCurrentPage(1); 
      },[project, searchProject])

  const tableColumns  : THeaderProps[] = [
    {
      key: "advertiser",
      width: "20%",
      title: t("project_table_advertiser"),
      isSortable: true,
      sortDirection: null,
      onSort: ({key, direction}: any) => {
        props.setSortingColumns({
          [key]: direction
        });
        // props.setSortingColumns((prev: any) => {
        //   return {
        //     ...prev,
        //     [key]: direction
        //   }
        // });
      },
    },
    {
      key: "name",
      width: "20%",
      title: t("project_table_projectName"),
      isSortable: true,
      sortDirection: null,
      onSort: ({key, direction}: any) => {
        props.setSortingColumns({
          [key]: direction
        });
        // props.setSortingColumns((prev: any) => {
        //   return {
        //     ...prev,
        //     [key]: direction
        //   }
        // });
      },
    },
    {
      key: "created_at",
      width: "20%",
      title: t("project_table_dateCreated"),
      isSortable: true,
      sortDirection: null,
      onSort: ({key, direction}: any) => {
        props.setSortingColumns({
          [key]: direction
        });
        // props.setSortingColumns((prev: any) => {
        //   return {
        //     ...prev,
        //     [key]: direction
        //   }
        // });
      },
      renderRowContent: (rowData: any) => (<> {new Date(rowData.created_at).toDateString()} </>),
    },
    {
      key: "total_analysis",
      width: "30%",
      title: t("project_table_numberOfAnalyse"),
      isSortable: true,
      sortDirection: null,
      onSort: ({key, direction}: any) => {
        props.setSortingColumns({
          [key]: direction
        });
        // props.setSortingColumns((prev: any) => {
        //   return {
        //     ...prev,
        //     [key]: direction
        //   }
        // });
      },
    },
    {
      key: "actions",
      width: "10%",
      title: "",
      isSortable: false,
      sortDirection: null,
      onSort: () => {},
      renderRowContent: (rowData: any) => (
        <EditProject
          project={rowData}
          onDelete={props.onDelete}
          onEdit={props.onEdit}
          onDialogOpen={onDialogOpen}
          onDialogClose={onDialogClose}
        />        
      ),
    }
  ];

  const rowListStyle = css`
    cursor: pointer !important;
  `;
  const inputRowStyles = css`
    padding-top: ${theme.spacing["4xl"]};
    display: flex;
    gap: ${theme.spacing["4xl"]};
    > div {
      min-width: 200px;
      max-width: 40%;
    }
  `;
  return (
    <>
    <div css={inputRowStyles}>
      <ProjectsSearch setSelectProject={setProject} showProjectName={true} setSearchProject={setSearchProject} />
    </div>    
    <ClassNames>
    {({ css }) => (
      <DataTable
        tableData={projectList || []}
        totalRows={(project || searchProject)? totalRowsRef.current: totalRows}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={(rowsPerPage) => {
          setRowsPerPage(rowsPerPage);
        }}
        handleRowClick={(rowData: any, event:React.MouseEvent) => handleRowClick(rowData, event)}        
        columns={tableColumns}
        isTableDataLoading={props.isTableDataLoading}
        tableRowClass={css(rowListStyle)}
        defaultPage={props.pageNo}
        setPageNo={props.setPageNo}
        defaultRowsPerPage={rowsPerPage}   
      />
    )}
  </ClassNames>
  </>
  );
}
